import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { colorSelected, drawingToolSelected, setDrawingInfo, setSelectedColor, setSelectedSize, sizeSelected } from "@/components/DrawingNav/drawingSlice";
import { navigationMenuLayout } from "@/components/Menu/menuSlice";
import { createGraphics, MIN_POINTS } from "@/lib/drawing-utils";
import { activeDrawingPoint } from '@/lib/map-state';
import Point from "@arcgis/core/geometry/Point";
import MapView from "@arcgis/core/views/MapView";
import { useCallback, useEffect, useRef } from "react";

/** Points are placed this far (px) above the view center, under the Crosshair overlay. */
const CROSSHAIR_OFFSET_Y = 60;

/**
 * Manages the in-progress drawing: the points placed so far, their preview graphics on the
 * view, and the measurement/shape details published to the drawing slice for saving.
 */
export function useDrawingPoints(view: MapView | null) {
    const dispatch = useAppDispatch();
    const color = useAppSelector(colorSelected);
    const size = useAppSelector(sizeSelected);
    const tool = useAppSelector(drawingToolSelected);
    const layout = useAppSelector(navigationMenuLayout);
    const points = useRef<Point[]>([]);

    const updateGraphics = useCallback(() => {
        if (!view) return;
        view.graphics.removeAll();
        const { graphics, ...measurements } = createGraphics(color, size, tool, points.current);
        view.graphics.addMany(graphics);
        if (points.current.length < MIN_POINTS[tool]) {
            dispatch(setDrawingInfo(null));
            return;
        }
        const shape = tool === 'point' ? activeDrawingPoint(points.current)!.toJSON() : points.current.map((point) => point.toJSON());
        dispatch(setDrawingInfo({ ...measurements, shape, type: tool, color, size }));
    }, [color, dispatch, size, tool, view]);

    useEffect(() => { updateGraphics(); }, [updateGraphics]);

    // Entering or leaving drawing mode starts a fresh drawing with default styling.
    useEffect(() => {
        dispatch(setSelectedColor('blue'));
        dispatch(setSelectedSize('small'));
        points.current = [];
        view?.graphics.removeAll();
        dispatch(setDrawingInfo(null));
    }, [dispatch, layout, view]);

    const addPoint = () => {
        if (!view) return;
        const point = view.toMap({ x: view.width / 2, y: view.height / 2 - CROSSHAIR_OFFSET_Y });
        if (!point) return;
        points.current.push(point);
        updateGraphics();
    };

    const undo = () => {
        points.current.pop();
        updateGraphics();
    };

    return { addPoint, undo };
}
